import { api } from './api';

/**
 * Notification API endpoints using RTK Query
 * Provides hooks for fetching and managing notifications for seller/brand users
 */
export const notificationApi = api.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Get notifications for the current user
     * @param {Object} params - Query parameters
     * @param {number} params.page - Page number (default: 1)
     * @param {number} params.limit - Items per page (default: 20)
     * @param {boolean} params.unreadOnly - Filter to unread only
     * @param {string} params.type - Filter by notification type
     * @param {string} params.category - Filter by category
     * @returns {Object} Notifications with pagination and unread count
     */
    getMyNotifications: builder.query({
      query: (params = {}) => ({
        url: '/notifications/me',
        params,
      }),
      providesTags: ['Notification'],
    }),

    /**
     * Get unread notification count
     * @returns {Object} { unreadCount: number }
     */
    getUnreadCount: builder.query({
      query: () => '/notifications/unread-count',
      providesTags: ['Notification'],
    }),

    /**
     * Mark specific notifications as read
     * @param {Object} body - Request body
     * @param {Array<string>} body.notificationIds - IDs of notifications to mark as read
     */
    markNotificationsRead: builder.mutation({
      query: (body) => ({
        url: '/notifications/mark-read',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Notification'],
    }),

    /**
     * Mark all notifications as read for the current user
     */
    markAllNotificationsRead: builder.mutation({
      query: () => ({
        url: '/notifications/mark-all-read',
        method: 'PUT',
      }),
      invalidatesTags: ['Notification'],
    }),

    /**
     * Delete a notification
     * @param {string} id - Notification ID to delete
     */
    deleteNotification: builder.mutation({
      query: (id) => ({
        url: `/notifications/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Notification'],
    }),
  }),
});

// Export hooks for usage in components
export const {
  useGetMyNotificationsQuery,
  useGetUnreadCountQuery,
  useMarkNotificationsReadMutation,
  useMarkAllNotificationsReadMutation,
  useDeleteNotificationMutation,
} = notificationApi;
